import React, { useState, useEffect } from "react";
import { Button } from "@heroui/react";
import CommonModal from "@/components/modal/CommonModal";

interface Transaction {
  id: number;
  date: string;
  details: string;
}

interface AddNoteModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentTransaction: Transaction | null;
  onSaveNote: (transactionId: number, note: string) => Promise<void>;
  getTransactionNote: (transactionId: number) => string;
}

const AddNoteModal: React.FC<AddNoteModalProps> = ({ 
  isOpen, 
  onOpenChange, 
  currentTransaction, 
  onSaveNote, 
  getTransactionNote 
}) => {
  const [note, setNote] = useState("");
  const [isLoading, setSaving] = useState(false);

  // Load existing note when modal opens or transaction changes
  useEffect(() => {
    if (isOpen && currentTransaction) {
      const existingNote = getTransactionNote(currentTransaction.id);
      setNote(existingNote);
    }
  }, [isOpen, currentTransaction, getTransactionNote]);

  const handleClose = () => {
    setNote("");
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    if (!currentTransaction || isLoading) return;
    
    try {
      setSaving(true);
      await onSaveNote(currentTransaction.id, note);
      handleClose();
    } catch (error) {
      console.error("Error saving note:", error);
    } finally {
      setSaving(false);
    }
  };

  const hasExistingNote = currentTransaction && getTransactionNote(currentTransaction.id).length > 0;

  return (
    <CommonModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={hasExistingNote ? "View/Edit Note" : "Add Note"}
      size="md"
      closeButton={false}
    >
      <p className="text-sm text-gray-500 mb-4">
        Enter details to remember about this transaction. Your note will be
        saved for future reference.
      </p>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={500}
        rows={4}
        className="w-full border border-gray-300 rounded-lg p-3 text-sm text-gray-700 placeholder:text-gray-400 resize-none"
        placeholder="Enter notes here..."
      />

      <div className="text-xs text-gray-400 mt-1 mb-4">
        {note.length} / 500 Characters
      </div>

      <div className="flex justify-end gap-3 mb-4">
        <Button
          onPress={handleClose}
          className="w-full px-4 py-2 rounded-lg border bg-transparent border-gray-300 text-sm font-medium text-gray-600"
        >
          Cancel
        </Button>
        <Button
          onPress={handleConfirm}
          disabled={isLoading}
          className="w-full px-4 py-2 rounded-lg bg-gray-800 text-white text-sm font-semibold disabled:opacity-50"
        >
          {isLoading ? "Saving..." : "Confirm"}
        </Button>
      </div>
    </CommonModal>
  );
};

export default AddNoteModal;